<template>
  <div ref="root" class="sat-popover" :class="{ _open: open }">
    <div class="sat-popover__trigger" @click="toggle">
      <slot name="trigger" :open="open" />
    </div>
    <transition name="sat-popover">
      <div v-if="open" ref="panel" class="sat-popover__panel" :style="panelStyle" @keydown.esc.stop="close">
        <slot :close="close" />
      </div>
    </transition>
  </div>
</template>
<script>
// Panel under a trigger: menus and small inline editors. Fixed positioned, so tables with overflow don't clip it.
// Closes on Escape, on a click outside and on page scroll; the content gets `close` to close it after an action.
export default {
  name: "SatPopover",
  props: {
    align: {
      type: String,
      default: "left",
      validator: (value) => ["left", "right"].includes(value),
    },
    width: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { open: false, rect: null };
  },
  computed: {
    panelStyle() {
      if (!this.rect) return {};
      const gap = 6;
      const style = { width: this.width ? `${this.width}px` : null };
      // Opens upwards when there is no room below, e.g. in the last rows of a table.
      if (window.innerHeight - this.rect.bottom < 340 && this.rect.top > 340) {
        style.bottom = `${window.innerHeight - this.rect.top + gap}px`;
      } else {
        style.top = `${this.rect.bottom + gap}px`;
      }
      if (this.align === "right") {
        style.right = `${Math.max(window.innerWidth - this.rect.right, 8)}px`;
      } else {
        style.left = `${Math.max(Math.min(this.rect.left, window.innerWidth - (this.width || 240) - 8), 8)}px`;
      }
      return style;
    },
  },
  beforeDestroy() {
    this.unlisten();
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      this.open ? this.close() : this.show();
    },
    show() {
      this.rect = this.$refs.root.getBoundingClientRect();
      this.open = true;
      this.$emit("open");
      setTimeout(() => {
        document.addEventListener("mousedown", this.onOutside);
        document.addEventListener("keydown", this.onKeydown);
        window.addEventListener("scroll", this.onScroll, true);
        window.addEventListener("resize", this.close);
      });
    },
    close() {
      if (!this.open) return;
      this.open = false;
      this.unlisten();
      this.$emit("close");
    },
    unlisten() {
      document.removeEventListener("mousedown", this.onOutside);
      document.removeEventListener("keydown", this.onKeydown);
      window.removeEventListener("scroll", this.onScroll, true);
      window.removeEventListener("resize", this.close);
    },
    onOutside(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) this.close();
    },
    onScroll(event) {
      // Scrolling inside the panel (a long list) keeps it open.
      if (!this.$refs.panel || !this.$refs.panel.contains(event.target)) this.close();
    },
    onKeydown(event) {
      if (event.key === "Escape") this.close();
    },
  },
};
</script>
<style lang="scss" scoped>
.sat-popover {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.sat-popover__panel {
  position: fixed;
  z-index: 1000;
  min-width: 220px;
  max-width: min(420px, calc(100vw - 32px));
  padding: 6px;
  border: 1px solid var(--sat-border);
  border-radius: 12px;
  background: var(--sat-card);
  box-shadow: var(--sat-shadow);
  text-align: left;
  white-space: normal;
}

.sat-popover-enter-active,
.sat-popover-leave-active {
  transition: opacity 0.12s, transform 0.12s;
}

.sat-popover-enter,
.sat-popover-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>

<style lang="scss">
// Menu items inside a popover (not scoped: the items live in the slot of the parent).
.sat-menu {
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
}

.sat-menu__label {
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--sat-muted);
}

.sat-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: 0;
  border-radius: 8px;
  font-size: 14px;
  text-align: left;
  color: var(--sat-text);
  background: transparent;
  cursor: pointer;

  &:hover:not(:disabled),
  &:focus-visible {
    background: var(--sat-hover);
    outline: none;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  &._active {
    color: var(--sat-accent);
    font-weight: 600;
  }

  &._danger {
    color: var(--sat-danger);
  }

  .sat-menu__check {
    width: 16px;
    color: var(--sat-accent);
  }

  .sat-menu__meta {
    margin-left: auto;
    font-size: 12px;
    color: var(--sat-muted);
  }
}

.sat-menu__divider {
  height: 1px;
  margin: 6px 4px;
  background: var(--sat-border);
}
</style>
